import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import { Camera, Upload, Sparkles, CheckCircle2, DollarSign, FileText, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function ExpenseSubmissionPage() {
  const queryClient = useQueryClient();
  const [isScanning, setIsScanning] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const [form, setForm] = useState({
    submitted_by: "staff.miracle@org.org",
    merchant_name: "",
    amount: "",
    expense_date: new Date().toISOString().split("T")[0],
    category: "Program Food Aid",
    description: "",
  });

  const ocrMutation = useMutation({
    mutationFn: async (imageStr) => {
      const res = await apiClient.post("/expenses/ocr", { image: imageStr });
      return res.data;
    },
    onSuccess: (data) => {
      setIsScanning(false);
      setForm((prev) => ({
        ...prev,
        merchant_name: data.merchant_name,
        amount: data.amount.toString(),
        expense_date: data.date,
        category: data.category,
      }));
      toast.success("Receipt scanned via Cloud OCR! Merchant and amount auto-filled.");
    },
    onError: () => {
      setIsScanning(false);
      toast.error("OCR extraction failed");
    },
  });

  const submitMutation = useMutation({
    mutationFn: async (data) => {
      const res = await apiClient.post("/expenses", {
        ...data,
        amount: parseFloat(data.amount),
        receipt_image_url: previewImage,
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["expenses"]);
      toast.success("Expense claim submitted for manager approval!");
      setForm({
        submitted_by: "staff.miracle@org.org",
        merchant_name: "",
        amount: "",
        expense_date: new Date().toISOString().split("T")[0],
        category: "Program Food Aid",
        description: "",
      });
      setPreviewImage(null);
    },
  });

  const handleSimulateCameraCapture = () => {
    setIsScanning(true);
    const mockImageData = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==";
    setPreviewImage(mockImageData);
    setTimeout(() => {
      ocrMutation.mutate(mockImageData);
    }, 1200);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6 font-sans">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Camera className="w-7 h-7 text-indigo-600" />
          Mobile Receipt Photo & Cloud OCR Capture
        </h1>
        <p className="text-xs text-slate-500 mt-1">
          Snap receipt photos on mobile to auto-extract vendor, date, and amount via Cloud OCR.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Camera / Upload Box */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm flex flex-col justify-between">
          <div>
            <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
              <Upload className="w-4 h-4 text-indigo-600" />
              Receipt Image Capture
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Supports offline PWA photo caching & background OCR extraction.
            </p>
          </div>

          <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center bg-slate-50 flex flex-col items-center justify-center space-y-3">
            {isScanning ? (
              <div className="space-y-3 py-4">
                <Sparkles className="w-10 h-10 text-amber-500 animate-spin mx-auto" />
                <p className="text-xs font-semibold text-indigo-600 animate-pulse">
                  Cloud OCR Scanning Receipt...
                </p>
              </div>
            ) : previewImage ? (
              <div className="space-y-2">
                <div className="w-24 h-24 bg-slate-200 rounded-lg flex items-center justify-center mx-auto text-xs text-slate-500 font-mono">
                  [Receipt Ready]
                </div>
                <span className="text-[11px] text-emerald-600 font-medium flex items-center justify-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" /> OCR Auto-Extracted
                </span>
              </div>
            ) : (
              <div className="space-y-2">
                <Camera className="w-12 h-12 text-slate-300 mx-auto" />
                <p className="text-xs text-slate-500">No receipt photo captured yet</p>
              </div>
            )}
          </div>

          <button
            onClick={handleSimulateCameraCapture}
            disabled={isScanning}
            className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold text-xs rounded-xl shadow-sm flex items-center justify-center gap-2 transition-all"
          >
            <Camera className="w-4 h-4" />
            {isScanning ? "Extracting Fields..." : "Snap Receipt / Upload Photo"}
          </button>
        </div>

        {/* Auto-filled Form */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4 shadow-sm">
          <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
            <FileText className="w-4 h-4 text-indigo-600" />
            Claim Details & Accounting GL Code
          </h2>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitMutation.mutate(form);
            }}
            className="space-y-4"
          >
            <div>
              <label className="text-xs font-medium text-slate-700">Merchant / Vendor</label>
              <input
                required
                type="text"
                placeholder="e.g. Target"
                value={form.merchant_name}
                onChange={(e) => setForm({ ...form, merchant_name: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-xs"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-700">Amount ($)</label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400" />
                  <input
                    required
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    className="w-full pl-7 pr-3 py-2 border border-slate-200 rounded-lg text-xs font-mono"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Date</label>
                <input
                  required
                  type="date"
                  value={form.expense_date}
                  onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-xs"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-700">GL Category / Program Code</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-xs"
              >
                <option value="Program Food Aid">Program Food Aid</option>
                <option value="Volunteer Supplies">Volunteer Supplies</option>
                <option value="Office & Operations">Office & Operations</option>
                <option value="Travel & Logistics">Travel & Logistics</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-700">Memo / Description</label>
              <textarea
                rows={2}
                placeholder="Itemized notes for manager review..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-lg text-xs"
              />
            </div>

            <button
              type="submit"
              disabled={submitMutation.isPending || !form.amount}
              className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold text-xs rounded-xl shadow-sm flex items-center justify-center gap-1.5"
            >
              <span>Submit Claim for Approval</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
